/**
 * src/lib/voice/useVoiceQueueState.js — React hook backing VoiceQueueTray.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md (V-3).
 *
 * Polls IndexedDB voice_recordings every `pollMs` ms (default 1000). Returns
 * the visible session list (FIFO by created_at, dismissed entries hidden) +
 * a `dismiss(recording_id)` action.
 *
 * Auto-fade: when a recording is observed in status 'ready' for the first
 * time, a per-id timer starts. After `fadeMs` (default 15000 — CEO decision
 * 2026-05-01), the recording is auto-dismissed locally. Status 'error' is
 * sticky and never auto-fades (CEO decision 2026-05-01: "sticky on error").
 *
 * Dismissal persistence (CEO ask 2026-05-03): the dismissed-id set is
 * mirrored to localStorage so a page refresh doesn't bring the card back
 * while the upload is still in flight in the background. The IDB row is
 * untouched — other tabs still see it, and the worker still drains it.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { getAllRecordings, updateRecording } from '../voiceQueue.js';

// Phase A (CEO 2026-05-03 evening UAT): adds 'uploaded' (registered with
// server, awaiting transcription) and 'classifying' (post-transcript,
// classifier running). Both visible so the user can see the long tail of
// processing instead of the card flashing 'ready' and disappearing.
const VISIBLE_STATUSES = new Set([
  'queued', 'uploading', 'uploaded', 'processing', 'classifying', 'ready', 'error',
]);
// IDB statuses that mean "still in flight" — when set AND upload_id present,
// the hook reaches out to voice.upload_progress to discover the real terminal.
const IN_FLIGHT_STATUSES = new Set(['uploaded', 'processing', 'classifying']);
const SERVER_POLL_THROTTLE_MS = 3000;
const DISMISSED_KEY = 'acn-voice-tray-dismissed-v1';
const DISMISSED_CAP = 200; // bound the set so localStorage doesn't grow unbounded

// Server status → IDB status. Keep terminal failures sticky.
function mapServerToIdb(serverStatus) {
  if (!serverStatus) return null;
  if (serverStatus === 'classified' || serverStatus === 'ready') return 'ready';
  if (serverStatus === 'error') return 'error';
  if (serverStatus === 'classifying') return 'classifying';
  if (serverStatus === 'processing') return 'processing';
  if (serverStatus === 'uploading' || serverStatus === 'queued') return 'uploaded';
  return null; // unknown — leave as-is
}

function loadDismissedFromStorage() {
  try {
    if (typeof localStorage === 'undefined') return new Set();
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.slice(-DISMISSED_CAP) : []);
  } catch {
    return new Set();
  }
}

function saveDismissedToStorage(set) {
  try {
    if (typeof localStorage === 'undefined') return;
    const arr = Array.from(set).slice(-DISMISSED_CAP);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(arr));
  } catch {
    // Quota exceeded / Safari private mode — non-fatal.
  }
}

export function useVoiceQueueState({
  pollMs = 1000,
  fadeMs = 15000,
  fetchProgress, // injectable — defaults to lazy import of voice-v2 helper
} = {}) {
  const [sessions, setSessions] = useState([]);
  const dismissedRef = useRef(loadDismissedFromStorage());
  const readyAtRef = useRef(new Map()); // recording_id → first-seen-as-ready timestamp
  const lastServerPollRef = useRef(new Map()); // recording_id → last server poll timestamp
  const fetchProgressRef = useRef(fetchProgress);
  fetchProgressRef.current = fetchProgress;

  const dismiss = useCallback((recordingId) => {
    dismissedRef.current.add(recordingId);
    saveDismissedToStorage(dismissedRef.current);
    setSessions((prev) => prev.filter((s) => s.recording_id !== recordingId));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    async function syncOneToServer(rec, now, getProgress) {
      // Only poll the server for recordings that have been registered
      // (upload_id) and are still in flight.
      if (!rec.upload_id) return;
      if (!IN_FLIGHT_STATUSES.has(rec.status)) return;
      const last = lastServerPollRef.current.get(rec.recording_id) || 0;
      if (now - last < SERVER_POLL_THROTTLE_MS) return;
      lastServerPollRef.current.set(rec.recording_id, now);
      try {
        const progress = await getProgress(rec.upload_id);
        if (!progress) return;
        const mapped = mapServerToIdb(progress.status);
        if (!mapped || mapped === rec.status) {
          // Even with no terminal change, propagate sub-status counters
          // (chunks_done, chunks_total, error_message) so the UI can render
          // granular progress.
          // Server returns error message under 'error' (legacy) or 'error_message'.
          const serverErr = progress.error_message ?? progress.error ?? null;
          if (
            progress.chunks_done !== rec.chunks_done ||
            progress.chunks_total !== rec.chunks_total ||
            serverErr !== rec.error_message
          ) {
            await updateRecording(rec.recording_id, {
              chunks_done: progress.chunks_done ?? rec.chunks_done,
              chunks_total: progress.chunks_total ?? rec.chunks_total,
              error_message: serverErr ?? rec.error_message,
              server_status: progress.status,
            });
          }
          return;
        }
        const serverErr = progress.error_message ?? progress.error ?? null;
        await updateRecording(rec.recording_id, {
          status: mapped,
          server_status: progress.status,
          chunks_done: progress.chunks_done ?? rec.chunks_done,
          chunks_total: progress.chunks_total ?? rec.chunks_total,
          error_message: serverErr ?? rec.error_message,
        });
      } catch (err) {
        // Server poll is best-effort. Don't surface as toast; just log.
        if (typeof console !== 'undefined') {
          console.warn('[useVoiceQueueState] server poll error:', err?.message);
        }
      }
    }

    async function getProgressLazy(uploadId) {
      if (typeof fetchProgressRef.current === 'function') {
        return fetchProgressRef.current(uploadId);
      }
      const mod = await import('../api/voice-v2.js');
      return mod.fetchUploadProgress(uploadId);
    }

    async function poll() {
      try {
        const all = await getAllRecordings();
        if (cancelled) return;
        const now = Date.now();

        // Sync server-side status for in-flight recordings (throttled per id).
        // Run in parallel — failures are caught per-recording inside.
        await Promise.all(all.map((r) => syncOneToServer(r, now, getProgressLazy)));

        // Re-read after server sync may have updated rows.
        const refreshed = await getAllRecordings();
        if (cancelled) return;

        // Track 'ready' first-seen and apply auto-fade.
        for (const r of refreshed) {
          if (r.status === 'ready' && !readyAtRef.current.has(r.recording_id)) {
            readyAtRef.current.set(r.recording_id, now);
          }
          if (r.status !== 'ready') {
            // If a row leaves 'ready' (re-queued?) drop the timer.
            readyAtRef.current.delete(r.recording_id);
          }
          if (
            r.status === 'ready' &&
            now - (readyAtRef.current.get(r.recording_id) || now) >= fadeMs
          ) {
            if (!dismissedRef.current.has(r.recording_id)) {
              dismissedRef.current.add(r.recording_id);
              saveDismissedToStorage(dismissedRef.current);
            }
          }
        }

        const visible = refreshed
          .filter((r) => VISIBLE_STATUSES.has(r.status))
          .filter((r) => !dismissedRef.current.has(r.recording_id))
          .sort((a, b) => (a.created_at || 0) - (b.created_at || 0));

        setSessions(visible);
      } catch (err) {
        // Polling is best-effort; surface in console for debugging only.
        console.warn('[useVoiceQueueState] poll error:', err?.message);
      }
    }

    poll();
    timer = setInterval(poll, pollMs);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [pollMs, fadeMs]);

  return { sessions, dismiss };
}

// Test seam — pure mapper, no React.
export const _internal = { mapServerToIdb, IN_FLIGHT_STATUSES };
